import Phaser from "phaser";
import { unlockAudio } from "../audio/sfx";
import { portalFfaCatalog } from "../data/portal-ffa-catalog";
import type { SummonKind } from "../data/portal-ffa-catalog";
import { SceneKey, assertTransition } from "../flow/game-flow";
import { FfaPresentation } from "../features/portal-ffa/view/ffa-presentation";
import { drawMapBackdrop } from "../features/portal-ffa/view/map-backdrop";
import {
  beginFieldGesture,
  cameraBoundsWithHudInset,
  cssPxToGamePx,
  endFieldGesture,
  updateFieldGesture,
  type FieldGestureState,
} from "../features/portal-ffa/view/camera-gesture";
import {
  createWorld,
  drainEvents,
  requestSummon,
  setTeamGoal,
  tickWorld,
  type SummonRequestResult,
  type WorldState,
} from "../features/portal-ffa";
import { shouldRestartOnQuit } from "../flow/entry";
import { sharedInput } from "../input/input-controller";
import {
  mountPortalFfaHud,
  wireReenter,
  type CameraViewRect,
} from "../ui/huds/portal-ffa";
import { markActiveScene } from "../ui/screen-state";

/**
 * Portal Master FFA — input + presentation; sim in features/portal-ffa/sim; VFX via shared module.
 */
export class PortalFfaScene extends Phaser.Scene {
  private world!: WorldState;
  private presentation!: FfaPresentation;
  private goalMarker!: Phaser.GameObjects.Arc;
  private hud!: ReturnType<typeof mountPortalFfaHud>;
  private disposed = false;
  private pinching = false;
  private pinchStartDist = 0;
  private pinchStartZoom = 1;

  /** When true, camera centers on player master each frame. */
  private cameraFollow = true;
  /** Last user control (field/zoom) wall-clock ms — refocus only after idle. */
  private lastControlMs = 0;
  private fieldGesture: FieldGestureState | null = null;
  /** 0..1 progress of return-to-master ease (1 = finished / hard follow). */
  private refocusT = 1;
  private refocusFromX = 0;
  private refocusFromY = 0;

  constructor() {
    super(SceneKey.PortalFfa);
  }

  create(): void {
    this.disposed = false;
    this.cameraFollow = true;
    this.lastControlMs = performance.now();
    this.fieldGesture = null;
    this.refocusT = 1;
    markActiveScene(SceneKey.PortalFfa);
    sharedInput.attach();

    this.world = createWorld();
    const cam = portalFfaCatalog.camera;

    this.refreshCameraBounds();
    this.cameras.main.setZoom(cam.zoomDefault);
    this.refreshCameraBounds();

    drawMapBackdrop(this);

    this.presentation = new FfaPresentation(this);
    this.goalMarker = this.add
      .circle(0, 0, 10, 0xffffff, 0.35)
      .setStrokeStyle(2, 0xffd27a)
      .setDepth(2);

    this.hud = mountPortalFfaHud({
      onSummon: (kind) => {
        unlockAudio();
        return requestSummon(this.world, 0, kind, { allowQueue: true });
      },
      onReenter: () => wireReenter(this.world),
      onQuit: () => this.quitToTitle(),
      onZoom: (delta) => {
        unlockAudio();
        this.noteControl();
        this.adjustZoom(delta);
      },
      onPinchStart: () => {
        unlockAudio();
        this.noteControl();
        this.fieldGesture = null;
        this.pinchStartZoom = this.cameras.main.zoom;
        this.pinching = true;
      },
      onPinchScale: (scale) => {
        this.noteControl();
        this.applyPinchScale(scale);
      },
      onPinchEnd: () => {
        this.pinching = false;
      },
      onFieldDown: (clientX, clientY) => {
        unlockAudio();
        this.noteControl();
        this.beginFieldFromClient(clientX, clientY);
      },
      onFieldMove: (clientX, clientY) => {
        this.noteControl();
        this.moveFieldFromClient(clientX, clientY);
      },
      onFieldUp: (clientX, clientY) => {
        this.noteControl();
        this.endFieldFromClient(clientX, clientY);
      },
      getViewRect: () => this.getCameraViewRect(),
    });

    // Phaser canvas pinch fallback (edges not covered by field-hit).
    this.input.on("pointerdown", (_pointer: Phaser.Input.Pointer) => {
      unlockAudio();
      if (this.input.pointer2.isDown) {
        this.noteControl();
        this.fieldGesture = null;
        this.beginPinch();
      }
    });

    this.input.on("pointermove", () => {
      if (this.input.pointer1.isDown && this.input.pointer2.isDown) {
        this.noteControl();
        this.fieldGesture = null;
        this.updatePinch();
      }
    });

    this.input.on("pointerup", () => {
      if (!this.input.pointer1.isDown || !this.input.pointer2.isDown) {
        this.pinching = false;
      }
    });

    this.input.on(
      "wheel",
      (
        _pointer: Phaser.Input.Pointer,
        _gos: unknown,
        _dx: number,
        dy: number,
      ) => {
        unlockAudio();
        const step = portalFfaCatalog.camera.zoomStep;
        this.noteControl();
        this.adjustZoom(dy > 0 ? -step : step);
      },
    );

    this.input.keyboard?.on("keydown", (ev: KeyboardEvent) => {
      unlockAudio();
      const map: Record<string, SummonKind> = {
        Digit1: "guardian",
        Digit2: "soldier",
        Digit3: "raider",
        Digit4: "chaser",
        Digit5: "hero",
        Digit6: "weapon",
      };
      const kind = map[ev.code];
      if (kind) {
        const result = requestSummon(this.world, 0, kind, { allowQueue: true });
        this.hud?.feedbackSummon(kind, result);
      }
    });

    this.presentation.syncViews(this.world);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(_t: number, delta: number): void {
    if (this.disposed) return;
    const t0 = performance.now();
    let eventsLen = 0;

    try {
      const pressed = sharedInput.consumePressed();
      if (pressed.cancel) {
        this.quitToTitle();
        return;
      }

      tickWorld(this.world, delta / 1000);
      const events = drainEvents(this.world.eventsOut);
      eventsLen = events.length;
      try {
        this.presentation.applyEvents(events);
        this.presentation.syncViews(this.world);
      } catch (presErr) {
        console.error("[PortalFfa] presentation failed", presErr);
      }
    } catch (err) {
      console.error("[PortalFfa] update failed", err);
      if (this.world) {
        this.world.message = { key: "hud.msgError" };
      }
    }

    // Range/aura rings clear from live entities — must not share presentation try
    // or a syncViews throw leaves ghost circles at death positions.
    try {
      this.presentation?.drawStaticOverlay(this.world);
    } catch (overlayErr) {
      console.error("[PortalFfa] overlay failed", overlayErr);
    }

    // HUD tracks sim independently — presentation throw must not freeze meters/minimap.
    try {
      this.hud?.sync(this.world);
    } catch (hudErr) {
      console.error("[PortalFfa] hud sync failed", hudErr);
    }

    // Camera follow always runs even if presentation threw (root cause of "unlock").
    this.refreshCameraBounds();
    this.updateCameraFollow(delta / 1000);

    const elapsed = performance.now() - t0;
    if (elapsed > 50) {
      const track = this.presentation?.trackingStats?.() ?? null;
      console.warn("[PortalFfa] frame stall", {
        ms: Math.round(elapsed),
        entities: this.world?.entities.length ?? 0,
        views: track?.views,
        dying: track?.dying,
        events: eventsLen,
        dt: delta,
      });
    }
  }

  private clientToGamePoint(
    clientX: number,
    clientY: number,
  ): { x: number; y: number } | null {
    const canvas = this.game.canvas;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
      x: ((clientX - rect.left) / rect.width) * this.scale.width,
      y: ((clientY - rect.top) / rect.height) * this.scale.height,
    };
  }

  private beginFieldFromClient(clientX: number, clientY: number): void {
    if (this.pinching) return;
    const pt = this.clientToGamePoint(clientX, clientY);
    if (!pt) return;
    const camMain = this.cameras.main;
    this.fieldGesture = beginFieldGesture(
      pt.x,
      pt.y,
      camMain.scrollX,
      camMain.scrollY,
    );
  }

  private moveFieldFromClient(clientX: number, clientY: number): void {
    if (!this.fieldGesture?.active || this.pinching) return;
    const pt = this.clientToGamePoint(clientX, clientY);
    if (!pt) return;
    const { dx, dy, becameDrag } = updateFieldGesture(
      this.fieldGesture,
      pt.x,
      pt.y,
    );
    if (becameDrag) {
      this.cameraFollow = false;
      this.refocusT = 1;
    }
    if (this.fieldGesture.dragged) {
      const camMain = this.cameras.main;
      camMain.scrollX -= dx / camMain.zoom;
      camMain.scrollY -= dy / camMain.zoom;
    }
  }

  private endFieldFromClient(clientX: number, clientY: number): void {
    if (!this.fieldGesture?.active) return;
    const pt = this.clientToGamePoint(clientX, clientY);
    if (pt) {
      updateFieldGesture(this.fieldGesture, pt.x, pt.y);
    }
    const result = endFieldGesture(this.fieldGesture);
    this.fieldGesture = null;
    if (result.isTap) {
      this.applyFieldTapFromGamePoint(result.x, result.y);
    }
  }

  private noteControl(): void {
    this.lastControlMs = performance.now();
  }

  private resolveHudInsetsGamePx(): { top: number; bottom: number } {
    const cam = portalFfaCatalog.camera as {
      hudInsetTopPx?: number;
      hudInsetBottomPx?: number;
    };
    const fallbackTop = Number(cam.hudInsetTopPx) || 170;
    const fallbackBottom = Number(cam.hudInsetBottomPx) || 150;
    const canvas = this.game.canvas;
    if (!canvas) return { top: fallbackTop, bottom: fallbackBottom };
    const rect = canvas.getBoundingClientRect();
    const topEl = document.querySelector(".ffa-top") as HTMLElement | null;
    const dockEl = document.querySelector(".ffa-dock") as HTMLElement | null;
    const topCss = topEl?.getBoundingClientRect().height ?? fallbackTop;
    const bottomCss = dockEl?.getBoundingClientRect().height ?? fallbackBottom;
    return {
      top: cssPxToGamePx(topCss, rect.height, this.scale.height),
      bottom: cssPxToGamePx(bottomCss, rect.height, this.scale.height),
    };
  }

  /** Allow panning map edges into the mid band not covered by top/bottom HUD. */
  private refreshCameraBounds(): void {
    const { width: mapW, height: mapH } = portalFfaCatalog.map;
    const insets = this.resolveHudInsetsGamePx();
    const b = cameraBoundsWithHudInset(
      mapW,
      mapH,
      this.cameras.main.zoom,
      insets.top,
      insets.bottom,
    );
    this.cameras.main.setBounds(b.x, b.y, b.width, b.height);
  }

  private freeLookReturnMs(): number {
    const c = portalFfaCatalog.camera as { freeLookReturnSec?: number };
    return Number(c.freeLookReturnSec) || 3.5;
  }

  private refocusTweenSec(): number {
    const c = portalFfaCatalog.camera as { refocusTweenSec?: number };
    return Math.max(0.05, Number(c.refocusTweenSec) || 0.45);
  }

  private beginRefocusFromCurrent(): void {
    const cam = this.cameras.main;
    this.refocusFromX = cam.midPoint.x;
    this.refocusFromY = cam.midPoint.y;
    this.refocusT = 0;
  }

  private updateCameraFollow(dt: number): void {
    if (!this.world) return;
    const player = this.world.teams[0];
    if (!player?.alive) {
      this.goalMarker.setVisible(false);
      return;
    }

    const master = this.world.entities.find((e) => e.id === player.masterId);
    if (!master) {
      console.warn("[PortalFfa] alive team missing master", player.masterId);
      this.goalMarker.setVisible(false);
      return;
    }

    this.goalMarker.setPosition(player.goalX, player.goalY);
    this.goalMarker.setVisible(true);

    if (!this.cameraFollow) {
      // Re-focus only after idle with no control — not on a drag-end timer alone.
      if (performance.now() - this.lastControlMs >= this.freeLookReturnMs() * 1000) {
        this.cameraFollow = true;
        this.beginRefocusFromCurrent();
      } else {
        return;
      }
    }

    if (this.refocusT < 1) {
      this.refocusT = Math.min(1, this.refocusT + dt / this.refocusTweenSec());
      const t = this.refocusT;
      const u = t * t * (3 - 2 * t);
      const x = this.refocusFromX + (master.x - this.refocusFromX) * u;
      const y = this.refocusFromY + (master.y - this.refocusFromY) * u;
      this.cameras.main.centerOn(x, y);
      return;
    }

    this.cameras.main.centerOn(master.x, master.y);
  }

  private applyFieldTapFromGamePoint(gx: number, gy: number): void {
    if (!this.world.teams[0]?.alive) return;
    const worldPoint = this.cameras.main.getWorldPoint(gx, gy);
    setTeamGoal(this.world, 0, worldPoint.x, worldPoint.y);
    this.presentation.pulseGoal(this.goalMarker);
  }

  private adjustZoom(delta: number): void {
    const { zoomMin, zoomMax } = portalFfaCatalog.camera;
    const next = Phaser.Math.Clamp(
      this.cameras.main.zoom + delta,
      zoomMin,
      zoomMax,
    );
    this.cameras.main.setZoom(next);
    this.refreshCameraBounds();
  }

  /** HUD / engine PinchTracker: scale relative to pinchStartZoom. */
  private applyPinchScale(scaleFromStart: number): void {
    const { zoomMin, zoomMax } = portalFfaCatalog.camera;
    this.cameras.main.setZoom(
      Phaser.Math.Clamp(this.pinchStartZoom * scaleFromStart, zoomMin, zoomMax),
    );
    this.refreshCameraBounds();
  }

  private beginPinch(): void {
    const a = this.input.pointer1;
    const b = this.input.pointer2;
    this.pinchStartDist = Phaser.Math.Distance.Between(a.x, a.y, b.x, b.y);
    this.pinchStartZoom = this.cameras.main.zoom;
    this.pinching = this.pinchStartDist > 8;
  }

  private updatePinch(): void {
    if (!this.pinching) {
      this.beginPinch();
      return;
    }
    const a = this.input.pointer1;
    const b = this.input.pointer2;
    const dist = Phaser.Math.Distance.Between(a.x, a.y, b.x, b.y);
    if (this.pinchStartDist < 8) return;
    this.applyPinchScale(dist / this.pinchStartDist);
  }

  getCameraViewRect(): CameraViewRect {
    const cam = this.cameras.main;
    const view = cam.worldView;
    return {
      x: view.x,
      y: view.y,
      w: view.width,
      h: view.height,
      zoom: cam.zoom,
      cx: cam.midPoint.x,
      cy: cam.midPoint.y,
    };
  }

  private quitToTitle(): void {
    if (this.disposed) return;
    // Direct share entry (`?entry=` / VITE_ENTRY): restart this proto, skip hub.
    if (shouldRestartOnQuit(SceneKey.PortalFfa)) {
      assertTransition(SceneKey.PortalFfa, SceneKey.PortalFfa);
      this.teardown();
      this.scene.start(SceneKey.PortalFfa);
      return;
    }
    assertTransition(SceneKey.PortalFfa, SceneKey.Title);
    this.teardown();
    this.scene.start(SceneKey.Title);
  }

  private teardown(): void {
    this.disposed = true;
    this.hud?.destroy();
    this.presentation?.destroy();
    this.input.off("pointerdown");
    this.input.off("pointermove");
    this.input.off("pointerup");
    this.input.off("wheel");
  }
}

export type { SummonRequestResult };
